import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { change, Field, formValueSelector, isDirty } from 'redux-form';
import { connect } from 'react-redux';
import moment from 'moment';
import CustomerInlineLabelInput from 'components/Customer/CustomerInlineLabelInput';
import { DidYouMeanCombo } from 'components/Common/DidYouMeans/DidYouMeanCombo';
import DateTimes from 'components/Common/DateTimes';
import { RenderDatePicker, NamePartsInput } from 'components/Common/Inputs';
import EntityType from 'common/Customers/EntityType';
import EntityIdType from 'common/Customers/EntityIdType';

const CustomerDetailsPanel = ({ isEditingCustomer, jurisdictions, customerType, dispatch, customerNameValue, customerNamePartsValue, entityType,
  isFormDirty
}) => {
  const [useFullName, setUseFullName] = useState(true);
  const [countryOfCitizenshipOrIncorpLabel, setcountryOfCitizenshipOrIncorp] = useState('');

  useEffect(() => {
    // Initialise component
    if (customerType === 0) {
      setcountryOfCitizenshipOrIncorp('Citizenship');
    } else {
      setcountryOfCitizenshipOrIncorp('Incorporation');
    }
  }, []);

  useEffect(() => {
    if (!isFormDirty) {
      return;
    }
    if (customerType === 1) {
      setUseFullName(true);
      dispatch(change('customerEditForm', 'customer.nameParts', null));

      setcountryOfCitizenshipOrIncorp('Incorporation');
      dispatch(change('customerEditForm', 'customer.entityType', 1));
    } else {
      dispatch(change('customerEditForm', 'customer.entityType', 100));
      setcountryOfCitizenshipOrIncorp('Citizenship');
    }
  }, [customerType]);

  useEffect(() => {
    if (useFullName) {
      dispatch(change('customerEditForm', 'customer.name', customerNameValue));
    } else {
      dispatch(change('customerEditForm', 'customer.name', ''));
    }
  }, [useFullName]);

  useEffect(() => {
    if (isFormDirty && entityType === 100) {
      dispatch(change('customerEditForm', 'customer.customerType', 0));
    }
  }, [entityType]);

  const getJurisdictionName = (country) => {
    if (country && jurisdictions && jurisdictions.length > 0) {
      const matches = jurisdictions.filter(x => x.code === country || x.name === country);
      if (matches && matches.length > 0) {
        return `${ matches[0].name }`;
      }
    }
    return country != null && country !== '' ? country : '';
  };

  const getDateFormat = date => (date && moment(date).format('MMM DD, YYYY')) ?? '';

  jurisdictions ??= [];

  const customerTypes = [
    { value: 0, description: 'Individual' },
    { value: 1, description: 'Entity' }
  ];

  const parseDescriptionToValue = (description, keyValuePairs) => {
    const requirement = keyValuePairs?.find(req => req.description === description);
    return requirement?.value ?? '';
  };

  const formatValueAsDescription = (value, keyValuePairs) => {
    const requirement = keyValuePairs?.find(req => req.value === value);
    return requirement?.description ?? '';
  };

  const showNameParts = customerType === 0;

  return (
    <div className="customer-details-panel">
      <div className="customer-form-panel">
        <h2 id="customer-details-header"><strong>Shareholder Details:</strong></h2>
        <div className="panel">
          <div className="panel-data">
            <div className="columns">
              <div className="col-6">
                {
                  !showNameParts ? (
                    <Field
                      name="name"
                      component={ CustomerInlineLabelInput }
                      isEditable={ isEditingCustomer }
                      label="Name"
                      id="customer-name"
                    />
                  ) : (
                    <div className="form-group columns">
                      <label className="form-label col-3" id="customer-name-label">Name</label>
                      <NamePartsInput
                        className="customer-name-parts-input"
                        disableFields={ !isEditingCustomer }
                        useFullNameExternal={ useFullName }
                        setUseFullNameExternal={ setUseFullName }
                        fullNameFieldName="name"
                        firstNameFieldName="nameParts.firstName"
                        middleNameFieldName="nameParts.middleName"
                        lastNameFieldName="nameParts.lastName"
                      />
                    </div>
                  )
                }
                <Field
                  name="customerIdentifier"
                  component={ CustomerInlineLabelInput }
                  isEditable={ isEditingCustomer }
                  label="ID"
                  id="customer-form-id"
                />
                <div className="form-group columns">
                  <label className="form-label col-3" id="customer-type-label">Type</label>
                  <span className="col-8">
                    <Field
                      name="customerType"
                      component="select"
                      disabled={ !isEditingCustomer }
                      className="form-select"
                      format={ value => formatValueAsDescription(value, customerTypes) }
                      parse={ value => parseDescriptionToValue(value, customerTypes) }
                      id="customer-type"
                    >
                      {customerTypes.map((item, index) => (
                        <option
                          key={ item.value }
                          id="customer-type"
                          value={ item.description }
                        >
                          {item.description}
                        </option>))}
                    </Field>
                  </span>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-3" id="customer-entity-type-label">Entity Type</label>
                  <span className="col-8">
                    <Field
                      name="entityType"
                      component="select"
                      disabled={ !isEditingCustomer || customerType === 0 }
                      className="form-select"
                      format={ value => formatValueAsDescription(value, EntityType.getSortedTypes()) }
                      parse={ value => parseDescriptionToValue(value, EntityType.getSortedTypes()) }
                      id="customer-entity-type"
                    >
                      <option disabled hidden />
                      <option value="">Select to clear the field...</option>
                      {EntityType.getSortedTypes().map((item, index) => (
                        <option
                          key={ item.value }
                          id="customer-entity-type"
                          value={ item.description }
                        >
                          {item.description}
                        </option>))}
                    </Field>
                  </span>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-3" id="customer-is-investor-label">Is Investor</label>
                  <label className="form-checkbox col-8">
                    <Field
                      name="isInvestor"
                      component="input"
                      type="checkbox"
                      disabled={ !isEditingCustomer }
                    />
                    <i className="form-icon" id="customer-is-investor" />
                  </label>
                </div>
              </div>
              <div className="col-6">
                <Field
                  name="emailAddress"
                  component={ CustomerInlineLabelInput }
                  isEditable={ isEditingCustomer }
                  label="Email Address"
                  id="customer-email-address"
                />
                <Field
                  name="phoneNumber"
                  component={ CustomerInlineLabelInput }
                  isEditable={ isEditingCustomer }
                  label="Phone Number"
                  id="customer-phone-number"
                />
                {!isEditingCustomer && <Field
                  name="dateOfBirth"
                  component={ CustomerInlineLabelInput }
                  isEditable={ isEditingCustomer }
                  label="Date of Birth"
                  format={ getDateFormat }
                  id="customer-dob"
                />}
                {isEditingCustomer && <div className="form-group columns">
                  <label className="form-label col-3" id="customer-dob-label">Date of Birth</label>
                  <span className="col-8" id="customer-dob">
                    <Field
                      component={ RenderDatePicker }
                      name="dateOfBirth"
                      className="form-input"
                      placeholder="MMM DD, YYYY"
                      dateFormat="MMM dd, yyyy"
                      { ... {
                        maxDate: DateTimes.today,
                      } }
                      format={ getDateFormat }
                    />
                  </span>
                </div>}
                <Field
                  name="cityOfBirth"
                  component={ CustomerInlineLabelInput }
                  isEditable={ isEditingCustomer }
                  label="City of Birth"
                  id="customer-city-of-birth"
                />
                <div className="form-group columns">
                  <label className="form-label col-3" id="customer-country-of-birth-label">Country of Birth</label>
                  <span className="col-8">
                    <Field
                      name="countryOfBirth"
                      component={ DidYouMeanCombo }
                      items={ jurisdictions?.map(x => x.name) }
                      disabled={ !isEditingCustomer }
                      className="form-select"
                      format={ getJurisdictionName }
                      id="customer-country-of-birth"
                    />
                  </span>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-3" id="customer-coc-coi-country-label">{countryOfCitizenshipOrIncorpLabel}</label>
                  <span className="col-8">
                    <Field
                      name="citizenshipCode"
                      component={ DidYouMeanCombo }
                      items={ jurisdictions?.map(x => x.name) }
                      disabled={ !isEditingCustomer }
                      className="form-select"
                      format={ getJurisdictionName }
                      id="customer-coc-coi-country"
                    />
                  </span>
                </div>
              </div>
            </div>
            <h3 id="customer-carf-details-header">CRS/CARF Details</h3>
            <div className="columns">
              <div className="col-6">
                <div className="form-group columns">
                  <label className="form-label col-3" id="customer-entity-id-type-label">Entity ID Type</label>
                  <span className="col-8">
                    <Field
                      name="entityIdType"
                      component="select"
                      disabled={ !isEditingCustomer }
                      className="form-select"
                      format={ value => formatValueAsDescription(value, EntityIdType.getSortedTypes()) }
                      parse={ value => parseDescriptionToValue(value, EntityIdType.getSortedTypes()) }
                      id="customer-entity-id-type"
                    >
                      <option disabled hidden />
                      <option value="">Select to clear the field...</option>
                      {EntityIdType.getSortedTypes().map(item => (
                        <option
                          key={ item.value }
                          value={ item.description }
                        >
                          {item.description}
                        </option>))}
                    </Field>
                  </span>
                </div>
              </div>
              <div className="col-6">
                <Field
                  name="entityIdNumber"
                  component={ CustomerInlineLabelInput }
                  isEditable={ isEditingCustomer }
                  label="Entity ID Number"
                  id="customer-entity-id-number"
                  maxLength={ 50 }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CustomerDetailsPanel.propTypes = {
  isEditingCustomer: PropTypes.bool,
  jurisdictions: PropTypes.arrayOf(PropTypes.shape()),
  customerType: PropTypes.number,
  dispatch: PropTypes.func.isRequired,
  customerNameValue: PropTypes.string,
  customerNamePartsValue: PropTypes.shape(),
  entityType: PropTypes.number,
  isFormDirty: PropTypes.bool
};

CustomerDetailsPanel.defaultProps = {
  jurisdictions: null,
  isEditingCustomer: false,
  customerType: null,
  customerNameValue: null,
  customerNamePartsValue: null,
  entityType: null,
  isFormDirty: false
};

const selector = formValueSelector('customerEditForm');

const mapStateToProps = state => ({
  customerType: selector(state, 'customer.customerType'),
  entityType: selector(state, 'customer.entityType'),
  isFormDirty: isDirty('customerEditForm')(state)
});

export default connect(mapStateToProps)(CustomerDetailsPanel);
